import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCheck } from "lucide-react";
import { useNotifications } from "@/context/NotificationContext";

// Group notifications by project (estimate_id) so the panel stays readable.
function groupByProject(notifications) {
  const groups = [];
  const index = {};
  for (const n of notifications) {
    const key = n.estimate_id || n.project_id || "_general";
    if (!(key in index)) {
      index[key] = groups.length;
      groups.push({ key, label: key === "_general" ? null : (n.project_name || n.estimate_id), items: [] });
    }
    groups[index[key]].items.push(n);
  }
  return groups;
}

export default function NotificationBell() {  const { notifications, unread, markOne, markAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md text-muted-foreground hover:text-gold hover:bg-muted/60 transition-colors"
        title="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-critical text-white text-[10px] font-semibold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            data-testid="notification-panel"
            className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto card-surface z-50 shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-card">
              <span className="font-medium text-sm">Notifications</span>
              {unread > 0 && (
                <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-gold flex items-center gap-1 hover:underline">
                  <CheckCheck size={13} /> Mark all read
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">No notifications yet.</div>
            ) : (
              <div>
                {groupByProject(notifications).map((group) => (
                  <div key={group.key} data-testid={`notification-group-${group.key}`}>
                    {group.label && (
                      <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-gold/70 bg-card sticky">
                        {group.label}
                      </div>
                    )}
                    {group.items.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => !n.read && markOne(n.id)}
                        data-testid={`notification-${n.id}`}
                        className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted/40 transition-colors ${n.read ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 shrink-0" />}
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{n.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
